import React from 'react';
import { Link } from 'react-router-dom';

const sdgGoals: { img: string; url: string }[] = [
    { img: "sd1.png", url: "https://www.un.org/sustainabledevelopment/poverty/" },
    { img: "sd2.png", url: "https://www.un.org/sustainabledevelopment/hunger/" },
    { img: "sd3.png", url: "https://www.un.org/sustainabledevelopment/health/" },
    { img: "sd4.png", url: "https://www.un.org/sustainabledevelopment/education/" },
    { img: "sd5.png", url: "https://www.un.org/sustainabledevelopment/gender-equality/" },
    { img: "sd8.png", url: "https://www.un.org/sustainabledevelopment/economic-growth/" }
];

const areas = [
    {
        title: "Food & Nutrition",
        img: "Food & Nutrition.png",
        description: "Ensuring food security and nutritional support for vulnerable communities.",
        details: "We work to combat hunger and malnutrition by providing nutritious meals, food kits, and sustainable food source training to families in need. Our initiatives focus on children, pregnant women, and the elderly to ensure they receive the vital nutrients required for healthy living."
    },
    {
        title: "Health Care Support",
        img: "Health care Support.png",
        description: "Providing access to essential healthcare services and medical aid.",
        details: "Our healthcare initiatives bring medical camps, free check-ups, and essential medicines to remote and underserved areas. We collaborate with hospitals and doctors to facilitate critical treatments and promote health awareness to prevent disease outbreaks."
    },
    {
        title: "Education Initiatives",
        img: "Education Initiatives.png",
        description: "Empowering minds through quality education and learning resources.",
        details: "We believe education is the key to breaking the cycle of poverty. Our programs provide scholarships, school supplies, and after-school tutoring to ensure every child has the opportunity to learn, grow, and build a brighter future."
    },
    {
        title: "Women Empowerment",
        img: "women Empowerment.png",
        description: "Fostering independence through skill training and leadership development.",
        details: "We support women in becoming self-reliant through vocational training, financial literacy workshops, and support for micro-enterprises. By empowering women, we strengthen entire families and communities."
    },
    {
        title: "Livelihood & Skill Development",
        img: "Livelihood & Skill Development.png",
        description: "Building sustainable livelihoods through vocational training and job placement.",
        details: "Our skill development centers offer training in various trades, from tailoring to computer literacy, equipping youth and adults with the skills needed to secure dignified employment or start their own businesses."
    },
    {
        title: "Community Development",
        img: "Community Development.png",
        description: "Strengthening community infrastructure and social cohesion.",
        details: "We engage in holistic community development projects, including sanitation improvement, safe water access, and infrastructure development. We work closely with local leaders to address specific community needs and foster a spirit of collaboration."
    },
];

const ThematicAreas: React.FC = () => {
    const [selectedArea, setSelectedArea] = React.useState<typeof areas[0] | null>(null);

    return (
        <section id="thematic-areas" className="py-20 bg-gray-50 relative">
            <div className="container mx-auto px-4">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h4 className="text-secondary font-bold uppercase tracking-widest mb-2 text-sm">Thematic Areas</h4>
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">
                        Where we <span className="text-primary">focus our impact</span>
                    </h2>
                    <p className="text-gray-600 text-lg">
                        Support in education & healthcare, women & child welfare guide every program we deliver across the community,
                        alongside livelihoods, digital inclusion, and climate-resilient practices.
                    </p>
                </div>

                {/* UN Sustainable Development Goals Section */}
                <div className="mb-16">
                    <div className="text-center max-w-4xl mx-auto mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Sustainable Development Goals
                        </h2>
                        <p className="text-gray-600 text-base">
                            Aligning with global goals for inclusive growth
                        </p>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4 max-w-6xl mx-auto px-4">
                        {sdgGoals.map((goal: { img: string; url: string }, index: number) => (
                            <div key={index} className="text-center group cursor-pointer flex justify-center">
                                <a
                                    href={goal.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-32 h-32 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300"
                                >
                                    <img
                                        src={`/images/${goal.img}`}
                                        alt="Sustainable Development Goal"
                                        className="w-full h-full object-contain drop-shadow-md hover:drop-shadow-xl transition-all duration-300"
                                        loading="lazy"
                                    />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {areas.map((area, index) => (
                        <div
                            key={index}
                            onClick={() => setSelectedArea(area)}
                            className="group relative bg-white rounded-3xl shadow-premium hover:shadow-premium-lg transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-100 animate-fade-in-up max-w-sm mx-auto w-full cursor-pointer"
                            style={{ animationDelay: `${index * 0.1}s` }}
                        >
                            {/* Image Container */}
                            <div className="relative h-64 bg-gray-50 flex items-center justify-center p-4 overflow-hidden">
                                <img
                                    src={`/images/${area.img}`}
                                    alt={area.title}
                                    className="max-w-full max-h-full object-contain transform group-hover:scale-110 transition-transform duration-700"
                                />
                                {/* Overlay Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>

                                {/* Content Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3 className="text-xl font-bold text-white mb-2 leading-tight">
                                        {area.title}
                                    </h3>
                                    <div className="w-12 h-1 bg-primary rounded-full transform origin-left group-hover:scale-x-150 transition-transform duration-500"></div>
                                </div>
                            </div>

                            {/* Hover Details / Learn More */}
                            <div className="px-6 py-4 bg-white flex justify-between items-center">
                                <span className="text-primary font-bold text-sm">Learn More</span>
                                <div className="p-2 bg-primary/10 rounded-full text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            {selectedArea && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
                    onClick={() => setSelectedArea(null)}
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col md:flex-row animate-slideUp"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Image Side */}
                        <div className="w-full md:w-1/2 h-64 md:h-auto bg-gray-50 flex items-center justify-center p-8 relative">
                            <img
                                src={`/images/${selectedArea.img}`}
                                alt={selectedArea.title}
                                className="max-w-full max-h-full object-contain drop-shadow-lg"
                            />
                            {/* Close button for mobile */}
                            <button
                                onClick={() => setSelectedArea(null)}
                                className="absolute top-4 right-4 md:hidden p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content Side */}
                        <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center relative">
                            {/* Close button for desktop */}
                            <button
                                onClick={() => setSelectedArea(null)}
                                className="absolute top-4 right-4 hidden md:block p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <h3 className="text-3xl font-bold text-gray-900 mb-4">
                                {selectedArea.title}
                            </h3>
                            <div className="w-20 h-1.5 bg-gradient-to-r from-primary to-secondary rounded-full mb-6"></div>

                            <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                {selectedArea.details}
                            </p>

                            <Link
                                to="/donation"
                                className="self-start relative group bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-light hover:to-secondary text-white px-8 py-3 rounded-full font-bold transition-all shadow-premium hover:shadow-premium-lg transform hover:scale-105 inline-block overflow-hidden text-base"
                            >
                                <span className="relative z-10">Donate Now</span>
                                <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ThematicAreas;
